/**
 * Vest chart feed symbols (#622): `<VEST_MARKET>.VEST`, e.g. `BTC-PERP.VEST`.
 *
 * The prod engine persists Vest's public trade tape for these markets as
 * Sierra `.scid` files, and the normal chart path charts them. They are
 * charts only: never tradeable (the engine and tradovate-service refuse
 * orders and bot signals for them), so the trading UI is disabled.
 *
 * Volume: the file stores qty * 10^sizeDecimals as an integer (BTC: 4), the
 * chart divides it back for display (`volumeDivisorFor`).
 */
import { useSymbolStore } from '../store/symbolStore';
import { cmeVolumeColor } from './cmeVolumeSpikes';

export const VEST_FEED_SUFFIX = '.VEST';

/** Vest's crypto markets (exchangeInfo asset "crypto"), by the base before -PERP. Mirrors utt-engine crypto/mod.rs. */
const VEST_CRYPTO_BASES = ['BTC', 'ETH', 'SOL', 'XRP', 'HYPE'];

/** Size decimals of known markets, for when the registry has not loaded yet. */
/** Vest exchangeInfo sizeDecimals (BTC 4, ETH 3, SOL 2). */
const KNOWN_SIZE_DECIMALS: Record<string, number> = { 'BTC-PERP': 4, 'ETH-PERP': 3, 'SOL-PERP': 2 };

export function isVestFeedSymbol(symbol: string | null | undefined): boolean {
  if (!symbol) return false;
  const s = symbol.trim().toUpperCase();
  return s.length > VEST_FEED_SUFFIX.length && s.endsWith(VEST_FEED_SUFFIX);
}

/** `BTC-PERP.VEST` -> `BTC-PERP`; null for anything else. */
export function vestFeedMarket(symbol: string | null | undefined): string | null {
  if (!isVestFeedSymbol(symbol)) return null;
  const s = (symbol as string).trim().toUpperCase();
  return s.slice(0, s.length - VEST_FEED_SUFFIX.length);
}

/** A Vest chart feed symbol that is a crypto market (24h session). Bare `*-PERP` names are never this. */
export function isVestCryptoFeedSymbol(symbol: string | null | undefined): boolean {
  const market = vestFeedMarket(symbol);
  if (!market || !market.endsWith('-PERP')) return false;
  return VEST_CRYPTO_BASES.includes(market.slice(0, -'-PERP'.length));
}

/** `BTC-PERP.VEST` -> `BTC`, `AAPL-USD-PERP.VEST` -> `AAPL`. */
export function vestFeedLabel(symbol: string): string {
  const market = vestFeedMarket(symbol) ?? symbol.toUpperCase();
  for (const suffix of ['-USD-PERP', '-PERP']) {
    if (market.endsWith(suffix) && market.length > suffix.length) return market.slice(0, -suffix.length);
  }
  return market;
}

/** Size decimals of a feed symbol: registry entry, else the known table, else null. */
export function vestSizeDecimals(symbol: string | null | undefined): number | null {
  if (!isVestFeedSymbol(symbol)) return null;
  const upper = (symbol as string).trim().toUpperCase();
  const def = useSymbolStore.getState().symbols.find((s) => s.fullSymbol.toUpperCase() === upper || s.base.toUpperCase() === upper);
  if (def && typeof def.sizeDecimals === 'number' && Number.isFinite(def.sizeDecimals)) return def.sizeDecimals;
  const market = vestFeedMarket(symbol);
  return market && market in KNOWN_SIZE_DECIMALS ? KNOWN_SIZE_DECIMALS[market] : null;
}

/** What to divide a candle's raw volume by for display (1 for every non-Vest symbol). */
export function volumeDivisorFor(symbol: string | null | undefined): number {
  const d = vestSizeDecimals(symbol);
  return d === null ? 1 : Math.pow(10, d);
}

/** Raw candle volume -> display units. */
export function displayVolume(symbol: string | null | undefined, raw: number | null | undefined): number {
  const v = raw || 0;
  const div = volumeDivisorFor(symbol);
  return div === 1 ? v : v / div;
}

/** A display volume as text: whole numbers for contracts, up to sizeDecimals places for Vest units. */
export function formatDisplayVolume(symbol: string | null | undefined, v: number): string {
  const d = vestSizeDecimals(symbol);
  if (d === null) return Math.round(v).toLocaleString('en-US');
  return v.toLocaleString('en-US', { maximumFractionDigits: Math.min(d, 4) });
}

/** The OHLC fields `continuousOpen` rewrites. */
interface Ohlc { open: number; high: number; low: number; close: number }

/**
 * One candle drawn continuous with the one before it (#626): open = the
 * previous candle's close, high/low widened to include that open. With no
 * previous close (first candle) the candle comes back unchanged. Returns a
 * copy when it changes anything, the same object otherwise.
 *
 * Display only: the engine opens each Vest candle at its first trade (gap to
 * the previous close median 12 pts on BTC 2m), Vest's own klines open at the
 * previous close. Strategies, detectors and the tuned settings stay on the
 * raw trade candles; only the price series gets this. One deliberate
 * exception: the 🪝 wick hook judges the drawn bar it is pinned to
 * (`wickRejection.ts::wickInputBars`).
 */
export function continuousBar<T extends Ohlc>(candle: T, prevClose: number | null | undefined): T {
  if (prevClose === null || prevClose === undefined || !Number.isFinite(prevClose)) return candle;
  if (candle.open === prevClose && candle.high >= prevClose && candle.low <= prevClose) return candle;
  return {
    ...candle,
    open: prevClose,
    high: Math.max(candle.high, prevClose),
    low: Math.min(candle.low, prevClose),
  };
}

/**
 * A time-ordered candle series with every candle opening at the previous
 * candle's close (`continuousBar`). The first candle is unchanged; a time gap
 * with no candles (Vest's zero-volume buckets) chains to the previous existing
 * candle. Always uses the RAW previous close, so it is idempotent and a
 * prepended history only changes the candle right after the join.
 */
export function continuousOpen<T extends Ohlc>(candles: readonly T[]): T[] {
  const out: T[] = new Array(candles.length);
  for (let i = 0; i < candles.length; i++) {
    out[i] = i === 0 ? candles[0] : continuousBar(candles[i], candles[i - 1].close);
  }
  return out;
}

/**
 * The live (last) candle of `candles` as the price series should draw it:
 * continuous with the candle before it. Same result as the last element of
 * `continuousOpen(candles)` without walking the series.
 */
export function continuousLastBar<T extends Ohlc>(candles: readonly T[]): T | undefined {
  const n = candles.length;
  if (n === 0) return undefined;
  return n === 1 ? candles[0] : continuousBar(candles[n - 1], candles[n - 2].close);
}

/**
 * The price series data for `symbol`: `continuousOpen` for Vest feed symbols,
 * the SAME array (identity) for everything else (CME/Sierra untouched).
 */
export function priceSeriesCandles<T extends Ohlc>(symbol: string | null | undefined, candles: T[]): T[] {
  return isVestFeedSymbol(symbol) ? continuousOpen(candles) : candles;
}

/**
 * The live bar the price series draws for `symbol`: `continuousLastBar` for
 * Vest feed symbols, the SAME last candle object for everything else.
 */
export function priceSeriesLastBar<T extends Ohlc>(symbol: string | null | undefined, candles: readonly T[]): T | undefined {
  if (!isVestFeedSymbol(symbol)) return candles.length > 0 ? candles[candles.length - 1] : undefined;
  return continuousLastBar(candles);
}

/** Fallback tick for a feed symbol the registry does not list (the engine's `chart::tick_for` default). */
const VEST_FALLBACK_TICK = 0.01;

/**
 * Tick size of a feed symbol: the registry entry's `tickSize` (the engine
 * fills it from the .scid header tag), else 0.01. null for every non-Vest
 * symbol, so callers keep their CME behaviour.
 */
export function vestTickSize(symbol: string | null | undefined): number | null {
  if (!isVestFeedSymbol(symbol)) return null;
  const upper = (symbol as string).trim().toUpperCase();
  const def = useSymbolStore.getState().symbols.find((s) => s.fullSymbol.toUpperCase() === upper || s.base.toUpperCase() === upper);
  const t = def?.tickSize;
  return typeof t === 'number' && Number.isFinite(t) && t > 0 ? t : VEST_FALLBACK_TICK;
}

/** Decimal places of a tick size (0.25 -> 2, 0.001 -> 3, 1 -> 0). */
export function tickDecimals(tick: number): number {
  if (!(tick > 0) || !Number.isFinite(tick)) return 2;
  const s = String(tick);
  if (s.includes('e-')) return Number(s.split('e-')[1]) || 2;
  return (s.split('.')[1] || '').length;
}

/** `v` rounded to the nearest multiple of `tick`, without float noise (0.1 * 3 -> 0.3). */
export function roundToTick(v: number, tick: number): number {
  if (!(tick > 0) || !Number.isFinite(v)) return v;
  return Number((Math.round(v / tick) * tick).toFixed(tickDecimals(tick)));
}

/**
 * The current-candle range label (#628). CME/Sierra: the drawn bar's range to
 * the quarter point, exactly as before. Vest feeds: the RAW aggregated
 * candle's range (the drawn bar is widened by `continuousOpen`, which is not
 * range that traded inside the bar), rounded to the symbol's tick and printed
 * with the tick's decimals. `raw` missing (or for another time) falls back to
 * the drawn bar, still at the tick.
 */
export function candleRangeLabel(
  symbol: string | null | undefined,
  drawn: { time?: unknown; high: number; low: number },
  raw?: { time?: unknown; high: number; low: number } | null,
): string {
  if (!isVestFeedSymbol(symbol)) {
    const rounded = Math.round((drawn.high - drawn.low) * 4) / 4;
    return rounded.toFixed(2);
  }
  const tick = vestTickSize(symbol) ?? VEST_FALLBACK_TICK;
  const src = raw && (raw.time === undefined || drawn.time === undefined || raw.time === drawn.time) ? raw : drawn;
  return roundToTick(src.high - src.low, tick).toFixed(tickDecimals(tick));
}

/**
 * The previous candle the live path redraws when a new Vest candle has just
 * opened (#626/#628): `candles` is the raw aggregated series, `prevCount` its
 * length at the last draw. Returns that candle as drawn (continuous with the
 * one before it), volume untouched, so the caller can redraw both the price
 * bar and its volume bar (colour follows the drawn candle). null for every
 * non-Vest symbol and whenever no new candle opened.
 */
export function vestPrevBarRedraw<T extends Ohlc>(
  symbol: string | null | undefined,
  candles: readonly T[],
  prevCount: number,
): T | null {
  if (!isVestFeedSymbol(symbol)) return null;
  if (candles.length < 2 || candles.length !== prevCount + 1) return null;
  return continuousLastBar(candles.slice(-3, -1)) ?? null;
}

/**
 * Volume pane points for a whole repaint (spike recolour, index-drift heal):
 * values from the RAW candles, colour from the bar the price series DRAWS
 * (`priceSeriesCandles`), so a Vest bar whose continuous open flips its
 * direction keeps the drawn colour (#628), same as the full redraw. Candles
 * with a non-numeric time are skipped. CME: `priceSeriesCandles` is the
 * identity, so this is exactly the old raw-candle colouring.
 */
export function volumePaneData<T extends Ohlc & { time: unknown; volume?: number | null }>(
  symbol: string | null | undefined,
  candles: readonly T[],
  spikes: ReadonlySet<number>,
): { time: T['time']; value: number; color: string }[] {
  const valid = candles.filter((c) => typeof c.time === 'number');
  const drawn = priceSeriesCandles(symbol, valid);
  return valid.map((c, i) => ({ time: c.time, value: displayVolume(symbol, c.volume), color: cmeVolumeColor(drawn[i], spikes) }));
}

/**
 * Range to hand `priceScale().setVisibleRange()` so the pane SHOWS `span`
 * centred on `center`.
 *
 * setVisibleRange only fills the scale's inner area: lightweight-charts adds
 * the scaleMargins (default top 0.2 / bottom 0.1) plus the series' autoscale
 * margins in px around it, so a 1000 range on BTC drew ~1600 top to bottom
 * (and off-centre, as the top margin is bigger). The margins are fixed px, so
 * the mapping is linear: measure what one applied range actually shows
 * (`shownFrom`/`shownTo`, the prices at the pane's bottom and top edge) and
 * solve for the range that shows exactly `span`. Null when the measurement is
 * unusable (pane not laid out yet), so the caller keeps the plain range.
 */
export function compensatedVisibleRange(
  center: number,
  span: number,
  applied: { from: number; to: number },
  shown: { from: number; to: number },
): { from: number; to: number } | null {
  const set = applied.to - applied.from;
  const vals = [center, span, applied.from, applied.to, shown.from, shown.to];
  if (!vals.every(Number.isFinite) || set <= 0 || span <= 0) return null;
  if (shown.to - shown.from <= set * 0.999) return null; // margins never shrink the view
  const marginTop = (shown.to - applied.to) / set;
  const marginBottom = (applied.from - shown.from) / set;
  if (marginTop < 0 || marginBottom < 0) return null;
  const inner = span / (1 + marginTop + marginBottom);
  const from = center - span / 2 + inner * marginBottom;
  return { from, to: from + inner };
}

/**
 * What the chart watermark shows after the symbol.
 *
 * The engine's signal-prediction ATR (with its min threshold) wins whenever it
 * exists. .VEST feeds have WCC off, so the engine never sends one; for those
 * only, fall back to the chart's own ATR(14) of the displayed timeframe, at
 * the market's price precision and without the threshold. Every other symbol
 * with no prediction shows nothing, as before.
 */
export function watermarkAtr(
  symbol: string | null | undefined,
  predictionAtr: number | undefined,
  chartAtr: number,
  precision: number
): { text: string; showThreshold: boolean } | null {
  if (predictionAtr !== undefined) return { text: `ATR ${predictionAtr.toFixed(1)}`, showThreshold: true };
  if (!isVestFeedSymbol(symbol)) return null;
  if (!(chartAtr > 0) || !Number.isFinite(chartAtr)) return null;
  return { text: `ATR ${chartAtr.toFixed(precision)}`, showThreshold: false };
}
